import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon, ArrowRightIcon, ArrowLeftIcon, PaperAirplaneIcon, DocumentTextIcon, UserIcon, AcademicCapIcon, LockClosedIcon, ClipboardDocumentIcon, EnvelopeIcon, ClockIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid'; 
import { CheckCircleIcon, CalendarIcon } from '@heroicons/react/24/solid';
import Axios from 'axios'; 

const CreateClientWizard = ({ isOpen, onClose, onCreateClient, availableTemplates, data }) => {
  // --- STATE ---
  const [step, setStep] = useState(1);
  
  // Form Data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    language: 'English',
    program: '', 
    deadline: '',
    deadlineOption: null 
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  // Document Lists
  const [documents, setDocuments] = useState([]);
  
  // Add Custom Doc State
  const [isAddingDoc, setIsAddingDoc] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocDesc, setNewDocDesc] = useState('');

  // Username and Password
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  // Constants for Styles
  const inputStyle = "w-full px-4 py-3 rounded-lg border-[1px] border-gray-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition-all text-left text-slate-700 placeholder-slate-400 bg-white";
  const checklistItemStyle = "flex items-center justify-between border-[1px] border-slate-200 rounded-xl transition-all group overflow-hidden hover:border-sky-300 hover:shadow-md bg-white shadow-sm";

  // Helper function to safely extract the localized string
  const getLocalizedValue = (rawField, userData) => {
    const userLang = userData?.preferredLanguage || 'english'; 
    
    if (!rawField) return '';
    if (typeof rawField === 'string') return rawField;
    
    if (rawField && typeof rawField === 'object') {
        if (userLang === 'french') {
            return rawField.fr || rawField.en || ''; 
        } else {
            return rawField.en || '';
        }
    }
    return '';
  };

  // Process templates to extract localized values
  const processedTemplates = React.useMemo(() => {
    if (!availableTemplates || availableTemplates.length === 0) {
      return [];
    }

    return availableTemplates.map(t => ({
      ...t,
      id: t._id || t.id,
      name: getLocalizedValue(t.name, data),
      description: getLocalizedValue(t.description, data),
      defaultDocs: (() => {
        let currentSection = null;
        return (t.documents || []).map((doc, index) => {
          const sectionLabel = getLocalizedValue(doc.section, data) || null;
          if (sectionLabel) currentSection = sectionLabel;
          return {
            id: doc._id || doc.id || index + 1,
            name: getLocalizedValue(doc.name, data),
            description: getLocalizedValue(doc.description, data),
            mandatory: doc.optional !== undefined ? !doc.optional : (doc.mandatory !== undefined ? doc.mandatory : true),
            locked: doc.locked !== undefined ? doc.locked : false,
            icon: doc.icon || 'custom',
            section: currentSection
          };
        });
      })()
    }));
  }, [availableTemplates, data]);

  // Use processed templates
  const templates = processedTemplates;

  // Auto-select first template on load to prevent validation lock
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
        handleSelectTemplate(templates[0]);
    }
  }, [templates]);

  // --- HANDLERS ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplateId(template.id);
    setDocuments(template.defaultDocs); 
  };

  const setQuickDeadline = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const dateString = date.toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, deadline: dateString, deadlineOption: `+${days}d` }));
  };

  const toggleMandatory = (id) => {
    setDocuments(prevDocs => 
      prevDocs.map(doc => {
        if (doc.id === id && !doc.locked) {
          return { ...doc, mandatory: !doc.mandatory };
        }
        return doc;
      })
    );
  };

  const updateDoc = (id, field, value) => {
    setDocuments(prevDocs =>
      prevDocs.map(doc => doc.id === id ? { ...doc, [field]: value } : doc)
    );
  };

  const handleDeleteDoc = (id) => {
    setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== id));
  };

  const handleAddCustomDoc = () => {
    if (!newDocName.trim()) return;
    
    const newDoc = {
      id: Date.now(), // Simple unique ID
      name: newDocName,
      description: newDocDesc,
      mandatory: true,
      locked: false,
      icon: 'custom'
    };

    setDocuments(prev => [...prev, newDoc]);
    setNewDocName('');
    setNewDocDesc('');
    setIsAddingDoc(false);
  };

  const handleNextStep = () => {
    if (step === 1) {
      // 🟢 VALIDATION LOGIC START
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!formData.firstName.trim()) {
        alert("Please enter the client's First Name.");
        return;
      }
      if (!formData.lastName.trim()) {
        alert("Please enter the client's Last Name.");
        return;
      }
      if (!formData.email.trim()) {
        alert("Please enter an Email Address.");
        return;
      }
      if (!emailRegex.test(formData.email)) {
        alert("Please enter a valid Email Address (e.g. name@example.com).");
        return;
      }
      if (!formData.deadline) {
        alert("Please select a Deadline date.");
        return;
      }
      if (!selectedTemplateId) {
        alert("Please select a Checklist Template.");
        return; 
      }
      // 🟢 VALIDATION LOGIC END

      setStep(2);
    } 
  };

  // Generate username from last name
  const generateUsername = () => {
    if (!formData.lastName || formData.lastName.length < 2) {
      return;
    }
    const firstTwoLetters = formData.lastName.slice(0, 2).toLowerCase();
    const randomNumbers = Math.floor(Math.random() * 900) + 100;
    const generatedUsername = `${firstTwoLetters}${randomNumbers}`;
    setUsername(generatedUsername);
    return generatedUsername;
  };

  // Generate random password
  const generatePassword = () => {
    const length = 6;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      generatedPassword += charset.charAt(randomIndex);
    }
    setPassword(generatedPassword);
    return generatedPassword;
  };

  // Auto-generate username and password when last name is entered
  useEffect(() => {
    if (formData.lastName && formData.lastName.length >= 2 && !username) {
      generateUsername();
    }
  }, [formData.lastName]);

  useEffect(() => {
    if (!password) {
      generatePassword();
    }
  }, []);

  const handleSendInvite = async () => {
    if (!data || !data.id) {
      setError('Consultant data not available');
      return;
    }

    // Validate required fields (Double check)
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    // Generate credentials if not already set
    let finalUsername = username;
    let finalPassword = password;

    if (!finalUsername) {
      if (formData.lastName && formData.lastName.length >= 2) {
        finalUsername = generateUsername();
      } else {
        setError('Last name must be at least 2 characters to generate username');
        return;
      }
    }

    if (!finalPassword) {
      finalPassword = generatePassword();
    }

    if (!finalUsername || !finalPassword) {
      setError('Failed to generate credentials');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('client_firstName', formData.firstName);
      formDataToSend.append('client_lastName', formData.lastName);
      formDataToSend.append('program', formData.program || '');
      formDataToSend.append('consultantEmail', data.email);
      formDataToSend.append('client_Username', finalUsername);
      formDataToSend.append('client_Password', finalPassword);
      formDataToSend.append('client_Email', formData.email);
      formDataToSend.append('client_Phone', ''); // Not in wizard, can be empty
      formDataToSend.append('subscription', data.subscription || '');
      formDataToSend.append('dateDeadline', formData.deadline || '');
      formDataToSend.append('docNumber', documents.length);
      formDataToSend.append('noDeadline', !formData.deadline);
      formDataToSend.append('forToComplete', JSON.stringify([])); // Empty array for forms
      formDataToSend.append('numberOfClients', (data.clients?.length || 0) + 1);
      formDataToSend.append('templateId', selectedTemplateId || '');
      formDataToSend.append('sendingRequired', true);
      formDataToSend.append('language', formData.language.toLowerCase());

      // Append documents
      documents.forEach((doc, index) => {
        formDataToSend.append(`documents[${index}][name]`, doc.name);
        formDataToSend.append(`documents[${index}][description]`, doc.description || '');
        formDataToSend.append(`documents[${index}][doc_upload]`, 'none');
        formDataToSend.append(`documents[${index}][optional]`, !doc.mandatory);
      });

      const response = await Axios.put(`${backEndUrl}/update/${data.id}`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      setIsLoading(false);
      
      // Extract the newly created client from the response
      const updatedUser = response.data;
      
      let newClient = null;
      if (updatedUser?.clients && Array.isArray(updatedUser.clients)) {
        newClient = updatedUser.clients.find(client => 
          client.client_Email === formData.email &&
          client.client_firstName === formData.firstName &&
          client.client_lastName === formData.lastName &&
          client._id && !client._id.toString().startsWith('temp_')
        );
        
        if (!newClient && updatedUser.clients.length > 0) {
          const lastClient = updatedUser.clients[updatedUser.clients.length - 1];
          if (lastClient._id && !lastClient._id.toString().startsWith('temp_')) {
            newClient = lastClient;
          }
        }
      }

      const finalPayload = newClient || {
        client_firstName: formData.firstName,
        _id: 'temp_' + Date.now(),
        client_lastName: formData.lastName,
        program: formData.program, // <--- ADDED TO OPTIMISTIC PAYLOAD
        client_Email: formData.email,
        client_Username: finalUsername,
        client_Password: finalPassword,
        language: formData.language.toLowerCase(),
        dateDeadline: formData.deadline || '',
        documents: documents.map(doc => ({
          name: doc.name,
          description: doc.description || '',
          optional: !doc.mandatory,
          status: 'not uploaded'
        })),
        fileStatus: 'uncomplete',
        lastViewed: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        archive: false,
        docStatus: documents.length === 0 ? 'complete' : 'uncomplete'
      };

      // Call the callback to add client to datatable immediately for instant feedback
      if (onCreateClient) {
        onCreateClient(finalPayload);
    } else {
        console.error("⚠️ onCreateClient prop is missing!");
    }
      
      // Show success screen briefly, then the parent will close the wizard
      setStep(3);
    } catch (error) {
      setIsLoading(false);
      setError(error.response?.data?.message || 'Failed to create client. Please try again.');
      console.error('Failed to create client:', error);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({ firstName: '', lastName: '', email: '', language: 'English', program: '', deadline: '', deadlineOption: null });
    setUsername('');
    setPassword('');
    setError(null);
    setIsLoading(false);
    if(templates.length > 0) handleSelectTemplate(templates[0]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
      
      {/* MAIN CARD */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col md:flex-row h-[750px] md:h-[700px] animate-fade-in-up">
        
        {/* LEFT SIDEBAR */}
        <div className="bg-slate-900 w-full md:w-1/3 p-8 text-white flex flex-col justify-between relative overflow-hidden">
          
          <div className="relative z-10 text-left">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-500/20">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <span className="font-bold text-2xl tracking-tight text-white"> ImmiSpace</span>
            </div>
            
            <h2 className="text-3xl font-bold mb-4 text-white text-left">New Application</h2>
            <p className="text-slate-400 text-sm mb-12 leading-relaxed text-left">Set up a secure portal for your client in less than 30 seconds.</p>

            {/* Stepper */}
            <div className="space-y-8 pl-1">
              {/* Step 1 */}
              <div className={`flex items-start gap-4 transition-all duration-300 ${step === 1 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border-2 transition-colors ${step > 1 ? 'bg-emerald-500 border-emerald-500 text-white' : step === 1 ? 'bg-sky-600 border-sky-500 text-white' : 'border-slate-600 text-slate-400'}`}>
                  {step > 1 ? <CheckIcon className="h-5 w-5" /> : '1'}
                </div>
                <div className="pt-1 text-left">
                  <h3 className="font-bold text-sm text-white">Client Details</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Name, Email & Language</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className={`flex items-start gap-4 transition-all duration-300 ${step === 2 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border-2 transition-colors ${step > 2 ? 'bg-emerald-500 border-emerald-500 text-white' : step === 2 ? 'bg-sky-600 border-sky-500 text-white' : 'border-slate-600 text-slate-400 bg-transparent'}`}>
                  {step > 2 ? <CheckIcon className="h-5 w-5" /> : '2'}
                </div>
                <div className="pt-1 text-left">
                  <h3 className="font-bold text-sm text-white">Review Checklist</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Customize documents</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className={`flex items-start gap-4 transition-all duration-300 ${step === 3 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border-2 transition-colors ${step === 3 ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-600 text-slate-400 bg-transparent'}`}>
                  {step === 3 ? <CheckIcon className="h-5 w-5" /> : '3'}
                </div>
                <div className="pt-1 text-left">
                  <h3 className="font-bold text-sm text-white">Done</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Invites sent</p>
                </div>
              </div>
            </div>
          </div>

          {/* Encryption Badge */}
          <div className="relative z-10 flex items-center gap-2 text-slate-500 text-xs mt-auto pt-8">
            <LockClosedIcon className="h-3 w-3" />
            <span>Client data is AES-256 encrypted.</span>
          </div>

          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-sky-600 opacity-10 rounded-full blur-3xl pointer-events-none"></div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="w-full md:w-2/3 flex flex-col bg-white relative">
          
          {/* Close Button */}
          <div className="absolute top-6 right-6 z-20">
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto p-8 md:p-12 custom-scrollbar">
            
            {/* STEP 1: DETAILS & TEMPLATE */}
            {step === 1 && (
              <div className="animate-fade-in space-y-8 text-left">
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Who is this for?</h1>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">First Name <span className="text-red-500">*</span></label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={inputStyle} placeholder="John" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={inputStyle} placeholder="Doe" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <PaperAirplaneIcon className="h-4 w-4 text-slate-400" />
                      </div>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={`${inputStyle} pl-10`} placeholder="john.doe@gmail.com" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Language</label>
                      <div className="relative">
                        <select name="language" value={formData.language} onChange={handleInputChange} className={`${inputStyle} appearance-none cursor-pointer`}>
                          <option value="English">🇬🇧 English</option>
                          <option value="French">🇫🇷 French</option>
                        
                        </select>
                      </div>
                    </div>

                    {/* NEW PROGRAM INPUT HERE */}
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Program / Stream</label>
                      <input 
                        type="text" 
                        name="program" 
                        list="program-options"
                        value={formData.program} 
                        onChange={handleInputChange} 
                        className={inputStyle}
                        placeholder="e.g. Express Entry"
                      />
                      <datalist id="program-options">
                        <option value="Express Entry (FSW)"></option>
                        <option value="Express Entry (CEC)"></option>
                        <option value="Spousal Sponsorship"></option>
                        <option value="Study Permit"></option>
                        <option value="Visitor Visa"></option>
                        <option value="Provincial Nominee"></option>
                        <option value="Citizenship"></option>
                      </datalist>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-bold text-slate-700">Deadline <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <button onClick={() => setQuickDeadline(30)} type="button" className={`text-xs px-2 py-0.5 rounded-md font-medium transition-colors ${formData.deadlineOption === '+30d' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>+30d</button>
                          <button onClick={() => setQuickDeadline(60)} type="button" className={`text-xs px-2 py-0.5 rounded-md font-medium transition-colors ${formData.deadlineOption === '+60d' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>+60d</button>
                        </div>
                    </div>
                    <input type="date" name="deadline" value={formData.deadline} onChange={handleInputChange} className={inputStyle} />
                  </div>
                </div>

                <div className="pt-2">
                  <label className="block text-sm font-bold text-slate-700 mb-3">Checklist Template</label>
                  {templates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-[1px] border-dashed border-gray-300 bg-gray-50 text-center">
                      <ClipboardDocumentIcon className="h-8 w-8 text-slate-300" />
                      <div>
                        <p className="text-sm font-semibold text-slate-500">No templates yet</p>
                        <p className="text-xs text-slate-400 mt-1">Create a template from your settings to get started.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {templates.map((t) => (
                        <div key={t.id} onClick={() => handleSelectTemplate(t)} className={`relative flex items-center p-4 rounded-xl border-[1px] cursor-pointer transition-all duration-200 ${selectedTemplateId === t.id ? 'border-sky-500 bg-sky-50/30' : 'border-gray-300 hover:border-gray-400 bg-white'}`}>
                          <div className="flex-1">
                            <h4 className={`font-bold text-sm ${selectedTemplateId === t.id ? 'text-sky-900' : 'text-slate-900'}`}>{t.name}</h4>
                            <p className={`text-xs mt-0.5 ${selectedTemplateId === t.id ? 'text-sky-700' : 'text-slate-500'}`}>{t.description}</p>
                          </div>
                          {selectedTemplateId === t.id && <CheckCircleIcon className="h-6 w-6 text-sky-500" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2: REVIEW CHECKLIST (UPDATED) */}
            {step === 2 && (
              <div className="animate-fade-in space-y-6 text-left">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-800 text-left">Review Checklist</h1>
                    <p className="text-slate-500 text-sm mt-1 text-left">Customize the document list for this client.</p>
                  </div>
                  <button onClick={() => setStep(1)} className="text-sm text-slate-500 hover:text-sky-600 font-medium flex items-center gap-1">
                    <ArrowLeftIcon className="h-4 w-4"/> Back
                  </button>
                </div>

                <div className="space-y-3 pb-4">
                  {documents.map((doc, index) => {
                    const isFirstInSection = doc.section && (index === 0 || documents[index - 1].section !== doc.section);
                    return (
                    <React.Fragment key={doc.id}>
                      {isFirstInSection && (
                        <div className="flex items-center gap-3 pt-3 pb-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{doc.section}</span>
                          <div className="flex-1 h-px bg-slate-100"></div>
                        </div>
                      )}
                    <div 
                      className={`${checklistItemStyle} items-center`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0 pl-4 pr-4 self-stretch py-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${doc.locked ? 'bg-white border-[1px] border-gray-300 text-slate-400' : 'bg-sky-50 border-[1px] border-sky-100 text-sky-600'}`}>
                          <DocumentTextIcon className="h-5 w-5"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={doc.name}
                            onChange={(e) => updateDoc(doc.id, 'name', e.target.value)}
                            className="w-full font-bold text-sm text-slate-700 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-sky-400 focus:outline-none py-0.5 transition-colors placeholder-slate-300"
                            placeholder="Document name"
                          />
                          <textarea
                            value={doc.description}
                            onChange={(e) => {
                              updateDoc(doc.id, 'description', e.target.value);
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            className="w-full text-xs text-slate-400 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-sky-300 focus:outline-none py-0.5 mt-0.5 transition-colors placeholder-slate-300 resize-none overflow-hidden leading-relaxed"
                            placeholder="Add requirements..."
                          />
                        </div>
                      </div>

                      <div className="flex items-center h-full shrink-0">
                        {/* TOGGLE SECTION */}
                        <div className="px-5 flex flex-col items-center justify-center gap-1 border-r border-slate-100 min-w-[100px]">
                          <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${doc.locked ? 'text-red-400' : doc.mandatory ? 'text-sky-600' : 'text-slate-300'}`}>
                            {doc.locked ? 'Required' : doc.mandatory ? 'Mandatory' : 'Optional'}
                          </span>
                          {!doc.locked ? (
                            <div
                              onClick={() => toggleMandatory(doc.id)}
                              className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors duration-200 ${doc.mandatory ? 'bg-sky-500' : 'bg-slate-200'}`}
                            >
                              <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-200 shadow-sm ${doc.mandatory ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </div>
                          ) : (
                            <div className="h-5 flex items-center">
                              <LockClosedIcon className="h-3 w-3 text-slate-300" />
                            </div>
                          )}
                        </div>

                        {/* DELETE SECTION */}
                        <div className="px-4 flex items-center justify-center">
                          {!doc.locked ? (
                            <button
                              onClick={() => handleDeleteDoc(doc.id)}
                              className="p-2 text-slate-200 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="Remove from checklist"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          ) : (
                            <div className="w-9 h-9"></div>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                    );
                  })}
                </div>

                {/* Inline Add Form */}
                {isAddingDoc ? (
                  <div className="p-4 bg-sky-50 border-[1px] border-sky-200 rounded-xl animate-fade-in">
                    <div className="flex gap-4">
                      <div className="flex-1 space-y-3">
                        <div>
                          <label className="text-[10px] font-bold text-sky-700 uppercase tracking-wider mb-1 block text-left">Document Name</label>
                          <input 
                            type="text" 
                            value={newDocName}
                            onChange={(e) => setNewDocName(e.target.value)}
                            placeholder="e.g. Birth Certificate" 
                            className="w-full px-3 py-2 text-sm rounded-lg border-[1px] border-sky-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none text-left bg-white"
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-sky-700 uppercase tracking-wider mb-1 block text-left">Description (Optional)</label>
                          <input 
                            type="text" 
                            value={newDocDesc}
                            onChange={(e) => setNewDocDesc(e.target.value)}
                            placeholder="e.g. Must be translated" 
                            className="w-full px-3 py-2 text-sm rounded-lg border-[1px] border-sky-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none text-slate-600 text-left bg-white"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 justify-end">
                        <button onClick={handleAddCustomDoc} className="bg-sky-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-sky-700 transition">Add</button>
                        <button onClick={() => setIsAddingDoc(false)} className="text-sky-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-sky-100 transition">Cancel</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button 
                    onClick={() => setIsAddingDoc(true)} 
                    className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 text-sm font-semibold hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-all flex items-center justify-center gap-2"
                  >
                    <PlusIcon className="h-5 w-5" /> Add Custom Document
                  </button>
                )}
              </div>
            )}

            {/* STEP 3: SUCCESS */}
            {step === 3 && (
              <div className="h-full flex flex-col items-center justify-center animate-fade-in px-8 py-10">
                {/* Check Icon */}
                <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-sm ring-4 ring-emerald-50">
                  <CheckIcon className="h-10 w-10 stroke-[3]" />
                </div>

                {/* Title */}
                <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">Success! Client Created</h2>

                {/* Email line */}
                <p className="text-slate-500 text-sm mb-8 text-center">
                  The document checklist and secure portal credentials have been sent to{' '}
                  <span className="font-semibold text-slate-800">{formData.email}</span>
                  {' '}so your client can start uploading files immediately.
                </p>

                {/* Info Cards */}
                <div className="w-full space-y-3 mb-8">
                  {/* Automation Active */}
                  <div className="flex items-start gap-4 bg-sky-50  border-sky-200 rounded-xl px-5 py-4">
                    <div className="w-9 h-9 bg-white  border-sky-200 rounded-lg flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                      <ClockIcon className="h-5 w-5 text-sky-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs font-extrabold text-sky-600 uppercase tracking-widest mb-1">Automation Active</p>
                      <p className="text-sm font-semibold text-slate-800 leading-relaxed">
                        If the file is incomplete after the deadline, ImmiSpace will automatically follow up with the client with a precise list of the missing documents.
                      </p>
                    </div>
                  </div>

                  {/* Deadline */}
                  <div className="flex items-center gap-4 bg-slate-50  border-slate-200 rounded-xl px-5 py-4">
                    <div className="w-9 h-9 bg-white  border-slate-200 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                      <CalendarIcon className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Deadline</p>
                      <p className="text-sm font-semibold text-slate-800">{formData.deadline || 'No date set'}</p>
                    </div>
                  </div>
                </div>

                <button onClick={resetAndClose} className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-3.5 rounded-lg font-bold shadow-lg transition-all text-sm">
                  Go to Client Dashboard
                </button>
              </div>
            )}

          </div>

          {/* BOTTOM ACTIONS BAR */}
          {step < 3 && (
            <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-4">
              {step === 1 && (
                <>
                  <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
                  <button onClick={handleNextStep} className="bg-sky-700 hover:bg-sky-800 text-white px-8 py-2.5 rounded-lg font-bold shadow-lg shadow-sky-900/10 transition-all flex items-center gap-2">Next Step</button>
                </>
              )}

              {step === 2 && (
                <div className="flex w-full items-center justify-between">
                   <div className="text-xs text-slate-500">
                        <span className="font-bold text-slate-700">{documents.filter(d => d.mandatory).length} items</span> will be requested.
                    </div>
                    <div className="flex gap-4">
                      <button onClick={() => setStep(1)} className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors">Back</button>
                      <button onClick={handleSendInvite} className="bg-sky-700 hover:bg-sky-800 text-white px-8 py-2.5 rounded-lg font-bold shadow-lg shadow-sky-900/10 transition-all flex items-center gap-2">
                        {isLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            Send Invitation <PaperAirplaneIcon className="h-4 w-4"/>
                          </>
                        )}
                      </button>
                    </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CreateClientWizard;